import { Component, EventEmitter, forwardRef, Input, OnChanges, Output, SimpleChanges, ViewChild } from '@angular/core';
import { NG_VALUE_ACCESSOR } from '@angular/forms';
import { differenceInCalendarDays } from 'date-fns';
import { NzDatePickerComponent } from 'ng-zorro-antd/date-picker';
import { NzMessageService } from 'ng-zorro-antd/message';
import dayjs from 'dayjs';
import { Task, TaskConfigField } from '@app/interfaces/lm/kanban-board';
import { KanbanBoardService } from '@services/lm/kanban-board.service';
import { ConfirmationService } from '@services/sys/confirmation.service';

@Component({
    selector: 'app-date-select',
    templateUrl: './date-select.component.html',
    styleUrl: './date-select.component.less',
    providers: [
        {
            provide: NG_VALUE_ACCESSOR,
            useExisting: forwardRef(() => DateSelectComponent),
            multi: true,
        },
    ],
})
export class DateSelectComponent implements OnChanges {
    @Input() clickToEdit = false; // 点击后才编辑
    @Input() submitLoading = false;
    @Input() options: string[] = [];
    @Input() formControlName = '';
    @Input() initialValue = '';
    @Input() required = false;
    @Input() nzShowTime = false;
    @Input() field?: TaskConfigField;
    @Input() taskInfo?: Task;
    @Input() inactiveUsers: boolean = false;
    @Output() valueChange = new EventEmitter();
    @ViewChild('datePickerComponent') datePickerComponent!: NzDatePickerComponent;
    isEditing = false;
    openMenu = false;

    currentValue = '';

    constructor(private message: NzMessageService,
                private kanbanBoardService: KanbanBoardService,
                private confirmationService: ConfirmationService) {
    }

    onChange: any = () => {
    };
    onTouch: () => void = () => null;

    writeValue(obj: any): void {
        this.currentValue = obj || '';
    }

    registerOnChange(fn: any): void {
        this.onChange = fn;
    }

    registerOnTouched(fn: any): void {
        this.onTouch = fn;
    }

    ngOnChanges(changes: SimpleChanges): void {
        const { submitLoading } = changes;
        if (submitLoading && !this.submitLoading) {
            this.isEditing = false;
        }
    }

    startEditing(): void {
        this.currentValue = this.initialValue;
        this.isEditing = true;
        setTimeout(() => {
            this.datePickerComponent?.open();
            this.openMenu = true;
        }, 200);
    }

    customValid(): boolean {
        if (!this.taskInfo || !this.field) {
            return true;
        }
        const { defaultValueRule } = this.field;
        const { startDate, dueDate } = this.taskInfo;
        const value = this.currentValue;
        const isDefaultField = this.kanbanBoardService.isDefaultField(defaultValueRule);
        if (isDefaultField) {
            const defaultConfig = defaultValueRule ? JSON.parse(defaultValueRule) : {};
            if (defaultConfig.fieldKey === 'startDate') {
                const isInvalid = this.nzShowTime
                    ? dayjs(value).isAfter(dayjs(dueDate))
                    : dayjs(value).isAfter(dayjs(dueDate), 'day');
                if (isInvalid) {
                    this.message.warning('The start date should not be later than the end date');
                    return false;
                }
            }

            if (defaultConfig.fieldKey === 'dueDate') {
                const isInvalid = this.nzShowTime
                    ? dayjs(value).isBefore(dayjs(startDate))
                    : dayjs(value).isBefore(dayjs(startDate), 'day');
                if (isInvalid) {
                    this.message.warning('The due date should not be earlier than start date');
                    return false;
                }
            }

            // if (defaultConfig.fieldKey === 'startDate' && dayjs(value).isSame(dayjs(dueDate), 'day')) {
            //     this.message.warning('Start date and due date cannot be the same day');
            //     return false;
            // }

            // if (defaultConfig.fieldKey === 'dueDate' && dayjs(value).isSame(dayjs(startDate), 'day')) {
            //     this.message.warning('Start date and due date cannot be the same day');
            //     return false;
            // }
        }

        return true;
    }

    changeValue(): void {
        if (!this.nzShowTime) {
            const date = new Date(this.currentValue);
            const timeRage = this.field?.fieldName === 'Due Date' ? 17 : 9;
            date.setUTCHours(timeRage, 0, 0, 0);
            const formattedValue = date.toISOString();
            this.onChange(formattedValue);
        } else {
            this.onChange(this.currentValue);
        }
        if (!this.clickToEdit) {
            return;
        }
        if (this.required && !this.currentValue) {
            this.currentValue = this.initialValue;
            this.message.error('This field is required');
        }
        if (!this.customValid()) {
            this.currentValue = this.initialValue;
        }
        this.confirmationService.confirmUpdateTask(this.inactiveUsers, () => {
            setTimeout(() => {
                if (this.currentValue !== this.initialValue) {
                    if (!this.nzShowTime && (this.field?.fieldName === 'Start Date'||this.field?.fieldName === 'Due Date')) {
                        const date = new Date(this.currentValue);
                        const timeRage = this.field?.fieldName === 'Due Date' ? 17 : 9;
                        date.setUTCHours(timeRage, 0, 0, 0);
                        const formattedValue = date.toISOString();
                        this.currentValue = formattedValue;
                    }
                    this.valueChange.emit(this.currentValue);
                }
                this.isEditing = false;
            }, 200);
        }, () => { this.isEditing = false; this.currentValue = this.initialValue; });
    }

    disabledDate = (current: Date): boolean => {
        if (!this.field || !this.taskInfo) {
            return false;
        }
        const { defaultValueRule } = this.field;
        const isDefaultField = this.kanbanBoardService.isDefaultField(defaultValueRule);
        const defaultConfig = defaultValueRule ? JSON.parse(defaultValueRule) : {};
        if (isDefaultField && ['startDate', 'endDate'].includes(defaultConfig.fieldKey)) {
            // 开始时间、结束时间不能选择今天之前的时间
            return differenceInCalendarDays(current, new Date()) < 0;
        }
        return false;
    }

    openChange(visible: boolean): void {
        if (!visible && this.isEditing) {
            this.isEditing = false;
        }
    }

    closeDropdown(): void {
        this.openMenu = false;
        this.datePickerComponent?.close();
    }
}
